/**
 * Commerce · Products — Product 360 Workspace (Wave 1).
 * Contract-driven per Object 009 · Product. Reuses existing primitives only.
 */
import { useMemo, useState } from "react";
import { Star } from "lucide-react";
import { Card, TextInput, Button } from "../../components/lib";
import { TabBar, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { AuditTrail } from "../../components/AuditTrail";
import { useDeepLink, useAutoOpen, RelatedLinksBlock } from "../../lib/integration";
import { linksForProductSku } from "../../mocks/entityLinks";
import { PRODUCTS, Product, ProductStatus, PROMOS } from "../../mocks/commerceData";
import { BATCHES } from "../../mocks/operationsData";
import { FORMULAS } from "../../mocks/rndData";
import { CAMPAIGNS } from "../../mocks/marketingData";

const STATUS_LABEL: Record<ProductStatus, string> = {
  live: "Live", draft: "Draft", archived: "Archived", "coming-soon": "Coming soon",
};
const STATUS_TINT: Record<ProductStatus, { bg: string; fg: string }> = {
  live:          { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  draft:         { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"     },
  archived:      { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"      },
  "coming-soon": { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
};

type WorkspaceTab = "overview" | "rd" | "inventory" | "financials" | "marketing" | "documents" | "audit";

const PRODUCT_SCHEMA: FieldSpec[] = [
  { key: "name",       label: "Product name",  kind: "text",     placeholder: "Nocturne Amber" },
  { key: "sku",        label: "SKU",           kind: "text",     placeholder: "ORV-NA-050" },
  { key: "family",     label: "Family",        kind: "select",   options: [
      { value: "eau-de-parfum", label: "EDP" },
      { value: "discovery",     label: "Discovery" },
      { value: "gift-set",      label: "Gift set" },
  ]},
  { key: "priceEGP",   label: "Price (EGP)",   kind: "number",   placeholder: "2900" },
  { key: "status",     label: "Status",        kind: "select",   options: [
      { value: "draft",       label: "Draft" },
      { value: "coming-soon", label: "Coming" },
      { value: "live",        label: "Live" },
      { value: "archived",    label: "Archived" },
  ]},
  { key: "launchDate", label: "Launch date",   kind: "text",     placeholder: "2026-03-15" },
  { key: "season",     label: "Season",        kind: "select",   options: [
      { value: "spring",   label: "Spring" },
      { value: "summer",   label: "Summer" },
      { value: "autumn",   label: "Autumn" },
      { value: "winter",   label: "Winter" },
      { value: "all-year", label: "All year" },
  ]},
  { key: "lifecycle",  label: "Lifecycle",     kind: "select",   options: [
      { value: "idea",          label: "Idea" },
      { value: "formula_dev",   label: "Formula" },
      { value: "blind_testing", label: "Blind" },
      { value: "pilot",         label: "Pilot" },
      { value: "launch_ready",  label: "Ready" },
      { value: "launched",      label: "Launched" },
      { value: "retired",       label: "Retired" },
  ]},
  { key: "notes",      label: "Description",   kind: "textarea", placeholder: "Amber, oud, saffron…" },
  { key: "hero",       label: "Image URL",     kind: "text",     placeholder: "https://…" },
];

export default function Products() {
  const { q: initialQ, open, clearOpen } = useDeepLink();
  const [tab, setTab] = useState<ProductStatus | "all">("all");
  const [q, setQ] = useState(initialQ);
  const [selected, setSelected] = useState<Product | null>(null);
  const [wsTab, setWsTab] = useState<WorkspaceTab>("overview");
  const [editorOpen, setEditorOpen] = useState<"create" | "edit" | null>(null);
  useAutoOpen(open, PRODUCTS, (p) => p.id, setSelected);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: PRODUCTS.length };
    PRODUCTS.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => PRODUCTS.filter((p) =>
      (tab === "all" || p.status === tab) &&
      (!q || p.name.toLowerCase().includes(q.toLowerCase()) || p.sku.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  const linkedBatches   = useMemo(() => selected ? BATCHES.filter((b) => b.sku === selected.sku)                                                  : [], [selected]);
  const linkedFormulas  = useMemo(() => selected ? FORMULAS.filter((f) => f.productId === selected.id)                                            : [], [selected]);
  const linkedCampaigns = useMemo(() => selected ? CAMPAIGNS.filter((c: any) => c.productSku === selected.sku || c.sku === selected.sku)          : [], [selected]);
  const linkedPromos    = useMemo(() => selected ? PROMOS.filter((p) => (p.label || "").toLowerCase().includes((selected.name || "").toLowerCase().split(" ")[0])) : [], [selected]);
  const marketingSpend  = useMemo(() => linkedCampaigns.reduce((s: number, c: any) => s + (c.budgetEGP || 0), 0), [linkedCampaigns]);
  const revenue         = useMemo(() => selected ? (selected.sold30d || 0) * (selected.priceEGP || 0) : 0, [selected]);
  const cogsPerUnit     = useMemo(() => linkedBatches.length ? linkedBatches.reduce((s, b: any) => s + (b.costEGPPerUnit || b.costEGP || 0), 0) / linkedBatches.length : 0, [linkedBatches]);
  const grossMargin     = revenue > 0 && selected ? ((revenue - (cogsPerUnit * selected.sold30d)) / revenue) * 100 : 0;
  const roas            = marketingSpend > 0 ? revenue / marketingSpend : 0;

  return (
    <div className="space-y-6" data-testid="cm-products">
      <Card testId="cm-products-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search by SKU or name…" testId="cm-products-search" /></div>
          <Button testId="cm-product-add" onClick={() => { setSelected(null); setEditorOpen("create"); }}>+ New product</Button>
        </div>
        <div className="mt-4">
          <TabBar<ProductStatus | "all">
            testId="cm-products-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",         label: "All",         count: counts.all         },
              { key: "live",        label: "Live",        count: counts.live        },
              { key: "coming-soon", label: "Coming soon", count: counts["coming-soon"] },
              { key: "draft",       label: "Draft",       count: counts.draft       },
              { key: "archived",    label: "Archived",    count: counts.archived    },
            ]}
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cm-products-grid">
        {filtered.map((p) => {
          const tint = STATUS_TINT[p.status];
          return (
            <button
              key={p.id}
              data-testid={`cm-product-${p.id}`}
              onClick={() => { setSelected(p); setWsTab("overview"); }}
              className="text-left"
              style={{
                background: "var(--ph-surface-card)",
                border: "1px solid var(--ph-border-subtle)",
                borderRadius: "var(--ph-radius-md)",
                boxShadow: "var(--ph-shadow-card)",
                overflow: "hidden",
              }}
            >
              <div className="relative" style={{ background: p.hero, height: 140 }}>
                <div className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold uppercase"
                  style={{ background: tint.bg, color: tint.fg, letterSpacing: "0.14em", borderRadius: "var(--ph-radius-sm)" }}>
                  {STATUS_LABEL[p.status]}
                </div>
                {p.rating > 0 && (
                  <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5"
                    style={{ background: "rgba(255,255,255,0.9)", color: "var(--ph-ink-strong)", borderRadius: "var(--ph-radius-sm)" }}>
                    <Star size={9} fill="currentColor" style={{ color: "var(--ph-brand-primary)" }} /> {p.rating.toFixed(1)}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{p.sku}</div>
                <div className="text-sm font-semibold mt-1" style={{ color: "var(--ph-ink-strong)" }}>{p.name}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {p.priceEGP.toLocaleString()}</div>
                  <div className="text-[11px] tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>{p.stock} in stock</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Drawer open={Boolean(selected) && editorOpen === null} onClose={() => { setSelected(null); clearOpen(); }} title="Product 360" testId="cm-product-drawer" width={640}>
        {selected && (
          <div>
            <div style={{ background: selected.hero, height: 140, borderRadius: "var(--ph-radius-md)" }} className="mb-3" />
            <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.sku}</div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-2">{selected.name}</div>
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-[10px] font-semibold uppercase"
                style={{ letterSpacing: "0.14em", background: STATUS_TINT[selected.status].bg, color: STATUS_TINT[selected.status].fg, borderRadius: "var(--ph-radius-sm)" }}>
                {STATUS_LABEL[selected.status]}
              </span>
              <span className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {selected.priceEGP.toLocaleString()}</span>
              <span className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>· {selected.family}</span>
            </div>

            <div className="mb-4 overflow-x-auto">
              <Segmented<WorkspaceTab>
                testId="cm-product-ws-tabs"
                value={wsTab}
                onChange={setWsTab}
                options={[
                  { value: "overview",   label: "Overview"   },
                  { value: "rd",         label: "R&D"        },
                  { value: "inventory",  label: "Inventory"  },
                  { value: "financials", label: "Financials" },
                  { value: "marketing",  label: "Marketing"  },
                  { value: "documents",  label: "Docs"       },
                  { value: "audit",      label: "Audit"      },
                ]}
              />
            </div>

            {wsTab === "overview" && (
              <div data-testid="cm-product-ws-overview">
                <div className="text-sm mb-3" style={{ color: "var(--ph-ink-default)" }}>{selected.notes}</div>
                <Field label="Launch date" inline><span className="text-sm">{(selected as any).launchDate || "—"}</span></Field>
                <Field label="Season"      inline><span className="text-sm">{(selected as any).season || "—"}</span></Field>
                <Field label="Lifecycle"   inline><span className="text-sm">{(selected as any).lifecycle || (selected.status === "live" ? "launched" : selected.status)}</span></Field>
                <Field label="Stock"       inline><span className="text-sm tabular-nums">{selected.stock}</span></Field>
                <Field label="Sold (30d)"  inline><span className="text-sm tabular-nums">{selected.sold30d}</span></Field>
                <Field label="Rating"      inline>
                  <span className="inline-flex items-center gap-1 text-sm tabular-nums" style={{ color: "var(--ph-brand-primary)" }}>
                    <Star size={12} fill="currentColor" /> {selected.rating.toFixed(1)} · {selected.reviews} reviews
                  </span>
                </Field>
              </div>
            )}

            {wsTab === "rd" && (
              <div data-testid="cm-product-ws-rd">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Formulas · {linkedFormulas.length}</div>
                {linkedFormulas.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No formulas linked.</div>}
                {linkedFormulas.map((f) => (
                  <div key={f.id} data-testid={`cm-product-formula-${f.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                    <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{f.id} · {f.version}</div>
                    <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{f.status} · IFRA {f.ifraCompliant ? "OK" : "FAIL"} · EGP {f.costEGPPerLiter}/L</div>
                  </div>
                ))}
              </div>
            )}

            {wsTab === "inventory" && (
              <div data-testid="cm-product-ws-inv">
                <Field label="Current stock"   inline><span className="text-sm tabular-nums">{selected.stock}</span></Field>
                <Field label="Inventory value" inline><span className="text-sm tabular-nums">EGP {(selected.stock * (cogsPerUnit || selected.priceEGP * 0.4)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Days in stock"   inline><span className="text-sm tabular-nums">{selected.sold30d > 0 ? Math.round(selected.stock / (selected.sold30d / 30)) : "—"}</span></Field>
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Batches · {linkedBatches.length}</div>
                  {linkedBatches.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No batches for this SKU.</div>}
                  {linkedBatches.map((b) => (
                    <div key={b.id} data-testid={`cm-product-batch-${b.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{b.code}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{b.stage} · {b.units} units · started {b.startedRelative}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {wsTab === "financials" && (
              <div data-testid="cm-product-ws-fin">
                <Field label="Revenue (30d)"   inline><span className="text-sm tabular-nums">EGP {revenue.toLocaleString()}</span></Field>
                <Field label="COGS / unit"     inline><span className="text-sm tabular-nums">EGP {cogsPerUnit.toFixed(0)}</span></Field>
                <Field label="Gross margin"    inline><span className="text-sm tabular-nums" style={{ color: grossMargin >= 50 ? "var(--ph-success)" : "var(--ph-warning)" }}>{grossMargin.toFixed(1)}%</span></Field>
                <Field label="Profit (30d)"    inline><span className="text-sm tabular-nums">EGP {(revenue - cogsPerUnit * selected.sold30d).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></Field>
                <Field label="Marketing spend" inline><span className="text-sm tabular-nums">EGP {marketingSpend.toLocaleString()}</span></Field>
                <Field label="ROAS"            inline><span className="text-sm tabular-nums">{roas > 0 ? `${roas.toFixed(2)}×` : "—"}</span></Field>
              </div>
            )}

            {wsTab === "marketing" && (
              <div data-testid="cm-product-ws-mkt">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Campaigns · {linkedCampaigns.length}</div>
                {linkedCampaigns.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No campaigns linked.</div>}
                {linkedCampaigns.map((c: any) => (
                  <div key={c.id} data-testid={`cm-product-campaign-${c.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                    <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{c.name}</div>
                    <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{c.status} · budget EGP {(c.budgetEGP || 0).toLocaleString()}</div>
                  </div>
                ))}
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1 mt-3" style={{ color: "var(--ph-ink-muted)" }}>Promotions · {linkedPromos.length}</div>
                {linkedPromos.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No promotions linked.</div>}
                {linkedPromos.map((p) => (
                  <div key={p.id} data-testid={`cm-product-promo-${p.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                    <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{p.code} · {p.label}</div>
                    <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{p.discount} · {p.status}</div>
                  </div>
                ))}
              </div>
            )}

            {wsTab === "documents" && (
              <div data-testid="cm-product-ws-docs">
                <RelatedLinksBlock links={linksForProductSku(selected.sku)} testId="cm-product-related" />
              </div>
            )}

            {wsTab === "audit" && (
              <div data-testid="cm-product-ws-audit">
                <AuditTrail collection="products" recordId={selected.id} testId="cm-product-audit" />
              </div>
            )}

            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="cm-product-archive">Archive</Button>
              <Button testId="cm-product-edit" onClick={() => setEditorOpen("edit")}>Edit</Button>
            </div>
          </div>
        )}
      </Drawer>

      <RecordEditor<Product>
        open={editorOpen !== null}
        onClose={() => setEditorOpen(null)}
        mode={editorOpen === "edit" ? "edit" : "create"}
        title={editorOpen === "edit" ? "Edit product" : "New product"}
        collection="products"
        schema={PRODUCT_SCHEMA}
        initial={editorOpen === "edit" && selected ? (selected as unknown as Record<string, unknown>) : {}}
        onSaved={(row) => { if (editorOpen === "edit") setSelected(row as unknown as Product); }}
        testId="cm-product-editor"
      />
    </div>
  );
}
